<section [ngClass]="containerClass">
  <div
    *ngIf="config.showTabs && config.tabs?.length"
    class="mb-3 flex flex-col gap-3 border-b border-slate200 pb-3 lg:flex-row lg:items-center lg:justify-between"
  >
    <div class="flex flex-wrap gap-2">
      <button
        *ngFor="let tab of config.tabs"
        type="button"
        (click)="onTabChange(tab.value)"
        class="inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition"
        [ngClass]="
          activeTab === tab.value
            ? 'border-indigo600 bg-indigo600 text-white'
            : 'border-slate200 bg-white text-slate600 hover:border-slate300 hover:bg-slate50 hover:text-slate900'
        "
      >
        <span>{{ tab.label }}</span>
        <span
          *ngIf="tab.count !== undefined"
          class="rounded-full px-2 py-0.5 text-xs"
          [ngClass]="
            activeTab === tab.value ? 'bg-white/20 text-white' : 'bg-slate100 text-slate600'
          "
        >
          {{ tab.count }}
        </span>
      </button>
    </div>

    <div *ngIf="config.showMarkAll" class="flex justify-start lg:justify-end">
      <button
        type="button"
        (click)="markAllRead.emit()"
        class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-emerald600 bg-emerald600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald700"
      >
        <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>Mark All as Read</span>
      </button>
    </div>
  </div>

  <form [formGroup]="filterForm" class="flex flex-wrap items-stretch gap-2.5 sm:items-center lg:gap-3">
    <div *ngIf="config.showSearch" class="relative w-full sm:w-[220px] lg:w-[240px]">
      <span class="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center">
        <svg
          class="h-4 w-4 text-slate600"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
          ></path>
        </svg>
      </span>
      <input
        type="text"
        formControlName="search"
        [placeholder]="config.searchPlaceholder || 'Search'"
        style="font-size: 12px; font-weight: 400"
        class="f-12 h-9 w-full rounded-md border border-slate300 bg-white pl-9 pr-8 text-slate600 outline-none transition placeholder:text-slate400"
      />
      <button
        *ngIf="canClearFilter('search')"
        type="button"
        aria-label="Clear search"
        (click)="removeFilter('search', $event)"
        class="absolute inset-y-0 right-2.5 inline-flex cursor-pointer items-center text-slate400 hover:text-slate600"
      >
        <ng-container *ngTemplateOutlet="clearIcon"></ng-container>
      </button>
    </div>

    <!-- Tracked by key: a consumer that hands back a fresh config after a remote
         search must not have its selects torn down and rebuilt under the user. -->
    <div *ngFor="let filter of config.filters; trackBy: trackFilter" class="w-full sm:w-[calc(50%-0.3125rem)] lg:w-fit lg:min-w-[100px]">
      <app-select
        *ngIf="filter.type === 'select'"
        class="w-full"
        [form]="filterForm"
        [controlName]="filter.key"
        [options]="getSelectOptions(filter)"
        [placeholder]="filter.label"
        [searchable]="!!filter.searchable"
        [multiple]="!!filter.multiple"
        [clearable]="canClearFilter(filter.key)"
        [remoteSearch]="!!filter.remoteSearch"
        [disabled]="!!filter.disabled"
        [disableWhenEmpty]="!filter.sources?.length"
        [sources]="getFilterSources(filter)"
        [activeSource]="activeSourceOf(filter)"
        [fitDropdownToContent]="true"
        size="sm"
        (searchQueryChange)="filterSearch.emit({ key: filter.key, query: $event })"
        (activeSourceChange)="onSourceChange(filter, $event)"
      ></app-select>

      <div *ngIf="filter.type !== 'select'" class="relative">
        <input
          [type]="filter.type"
          [formControlName]="filter.key"
          [placeholder]="filter.placeholder || filter.label"
          class="h-9 w-full rounded-md border border-slate300 bg-white pl-4 pr-8 text-xs font-semibold leading-[14px] text-slate600 outline-none transition placeholder:font-medium placeholder:text-slate400 focus:border-slate500"
          [ngClass]="filter.type === 'date' ? '!text-xs !text-slate600 !font-semibold' : ''"
        />
        <button
          *ngIf="canClearFilter(filter.key)"
          type="button"
          [attr.aria-label]="'Clear ' + filter.label"
          (click)="removeFilter(filter.key, $event)"
          class="absolute inset-y-0 right-2.5 inline-flex cursor-pointer items-center text-slate400 hover:text-slate600"
        >
          <ng-container *ngTemplateOutlet="clearIcon"></ng-container>
        </button>
      </div>
    </div>

    <!-- The shared date picker in range mode: it writes ISO dates straight into the form,
         keeps itself on screen and offers the same 1900-2100 years as every other date. -->
    <div *ngIf="config.showDateRange" class="w-full sm:w-auto" data-date-range-picker>
      <app-date-picker-reusable
        mode="range"
        size="sm"
        [startControl]="filterForm.get('startDate')"
        [endControl]="filterForm.get('endDate')"
      ></app-date-picker-reusable>
    </div>

    <div class="order-3 flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto">
      <app-button-reusable
        *ngIf="config.showReset"
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto !bg-white rounded-md"
        variant="danger"
        appearance="outline"
        size="sm"
        [disabled]="resetDisabled"
        (clicked)="onReset()"
      >
        <span class="BBody inline-flex items-center font-semibold leading-none ">Reset all</span>
        <app-icon-reusable
          [icon]="resetIcon"
          [size]="16"
          class="ml-2 mt-0.5"
          [useCurrentColor]="true"
          [classes]="resetIconClasses"
        ></app-icon-reusable>
      </app-button-reusable>
    </div>

    <div
      *ngIf="config.showReassign"
      class="order-4 flex w-full flex-wrap items-center gap-2 sm:order-none sm:ml-auto sm:w-auto"
      [appTooltip]="reassignTooltip"
    >
      <app-button-reusable
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto"
        variant="primary"
        appearance="outline"
        size="md"
        [disabled]="reassignDisabled"
        (clicked)="onReassign()"
      >
        <!-- <app-icon-reusable
          [icon]="reassignIcon"
          [size]="16"
          [useCurrentColor]="true"
          [classes]="'text-inherit shrink-0'"
        ></app-icon-reusable> -->
        <span class="BBody inline-flex items-center font-semibold leading-none">Reassign Client</span>
      </app-button-reusable>
    </div>

    <div
      *ngIf="config.showExport"
      class="relative order-5 flex w-full flex-wrap items-center gap-2 sm:order-none sm:w-auto"
      [ngClass]="config.showReassign ? '' : 'sm:ml-auto'"
      [appTooltip]="exportPermission ? '' : 'Request administrator for access'"
    >
      <!-- Optional consumer-supplied actions immediately before Export. -->
      <div class="flex flex-wrap items-center gap-2 empty:hidden sm:w-auto">
        <ng-content select="[filterActionsBeforeExport]"></ng-content>
      </div>

      <app-button-reusable
        class="[&>button]:h-9 [&>button]:w-full [&>button]:px-4 [&>button]:py-2 sm:[&>button]:w-auto"
        [disabled]="exportDisabled || !exportPermission"
        [loading]="exportLoading"
        [ariaExpanded]="exportOptions.length ? isExportMenuOpen : null"
        variant="neutral"
        appearance="filled"
        size="sm"
        (clicked)="onExport($event)"
      >
        <app-icon-reusable
          [icon]="exportIcon"
          [size]="16"

          [useCurrentColor]="true"
          [classes]="exportIconClasses"
        ></app-icon-reusable>
        <span class="BBody inline-flex items-center font-semibold leading-none ml-2 ">Export</span>
        <svg
          *ngIf="exportOptions.length"
          class="ml-1 h-4 w-4 transition-transform"
          [class.rotate-180]="isExportMenuOpen"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="m6 8 4 4 4-4" />
        </svg>
      </app-button-reusable>

      <!-- Only when the consumer names formats; otherwise Export downloads at once. -->
      <app-action-menu
        *ngIf="exportOptions.length"
        class="empty:hidden"
        [isOpen]="isExportMenuOpen"
        [compact]="true"
        [items]="exportOptions"
        (itemSelected)="onExportOptionSelected($event)"
        (closed)="closeExportMenu()"
      ></app-action-menu>
    </div>

    <!-- Optional consumer-supplied actions, sit alongside Export on the right.
         Collapsed when nothing is projected, so it stops adding a gap after Export. -->
    <div class="order-6 flex w-full flex-wrap items-center gap-2 empty:hidden sm:order-none sm:w-auto">
      <ng-content select="[filterActions]"></ng-content>
    </div>
  </form>

  <div
    *ngIf="config.showFilterChips && activeFilterChips.length > 0"
    class="mt-3 flex flex-col gap-2.5 border-t border-slate200 pt-3 lg:flex-row lg:items-center"
  >
    <span class="text-sm font-medium text-slate500">Active filters</span>

    <div class="flex flex-1 flex-wrap gap-2">
      <button
        *ngFor="let chip of activeFilterChips"
        type="button"
        (click)="removeFilter(chip.key)"
        class="inline-flex cursor-pointer items-center gap-2 rounded-full bg-sky100 px-3 py-1.5 text-xs font-medium text-sky800 transition hover:bg-sky200"
      >
        <span>{{ chip.label }}</span>
        <span class="text-sm leading-none">×</span>
      </button>
    </div>

    <button
      *ngIf="config.showReset"
      type="button"
      (click)="onReset()"
      class="inline-flex cursor-pointer items-center text-sm font-medium text-slate500 transition hover:text-slate800"
    >
      Clear all
    </button>
  </div>
</section>

<!-- Same × the select draws for its own clear control. -->
<ng-template #clearIcon>
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
</ng-template>
